import { useContext, useState } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import { Button, Alert, Radio, Row, Col } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers";
import Joi from "@hapi/joi";
import { removeEmptyFields } from "lib/utils";

import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import { TInput } from "components/form/Field/TInput";
import { TPhoneInput } from "components/form/Field/TPhoneInput";

const contactPerson = Joi.object().keys({
  name: Joi.string().required(),
  mobile_phone: Joi.string().allow(null),
  email: Joi.string().allow(null),
});

const clientSchema = Joi.object({
  name: Joi.string().required().min(3),
  short_name: Joi.string().alphanum().min(3).max(30).required(),
  type: Joi.string().valid("Government", "Business", "Personal"),
  address: Joi.string().allow(""),
  office_phone: Joi.string().allow(""),
  mobile_phone: Joi.string().allow(""),
  email: Joi.string().allow(""),
  contact_person: Joi.array().items(contactPerson),
});

function AddClientForm(props) {
  const [error, setError] = useState("");
  const router = useRouter();
  const { handleSubmit, errors, control, reset, setValue } = useForm({
    defaultValues: {
      name: props.client?.name === null ? undefined : props.client?.name,
      short_name:
        props.client?.short_name === null
          ? undefined
          : props.client?.short_name,
      type: props.client?.type === null ? undefined : props.client?.type,
      address:
        props.client?.address === null ? undefined : props.client?.address,
      office_phone:
        props.client?.office_phone === null
          ? undefined
          : props.client?.office_phone,
      mobile_phone:
        props.client?.mobile_phone === null
          ? undefined
          : props.client?.mobile_phone,
      email: props.client?.email === null ? undefined : props.client?.email,
      contact_person:
        props.client?.contact_person === null
          ? undefined
          : props.client?.contact_person === undefined
          ? [{ name: undefined, mobile_phone: undefined, email: undefined }]
          : props.client?.contact_person,
    },
    resolver: joiResolver(clientSchema),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "contact_person",
  });
  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = useContext(NotificationStateContext);

  const onSubmit = async (values) => {
    removeEmptyFields(values);
    setError("");

    if (props.update) {
      setLoading("updateClient");
    } else {
      setLoading("createClient");
    }

    try {
      if (props.update) {
        await apiAxios.put(`/clients/${props.client.id}`, values);
        finishLoading("updateClient");
        setSuccessMessage("Succesfully updated client");
        router.push(`/clients/[id]`, `/clients/${props.client.id}`);
      } else {
        const newClient = await apiAxios.post("/clients", values);
        finishLoading("addClient");
        setSuccessMessage("Succesfully added client");
        router.push(`/clients/[id]`, `/clients/${newClient.data.data.id}`);
      }

      clearSuccessMessage();
      reset();
    } catch (e) {
      {
        props.update
          ? finishLoading("updateClient")
          : finishLoading("addClient");
      }
      setError(e.response.data.message);
    }
  };
  return (
    <StyledContainer errors={errors}>
      {error && (
        <div className="alert">
          <Alert message={error} type="error" showIcon />
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={4}>
          <Col flex={1}>
            <TInput
              label="Name"
              errors={errors}
              name="name"
              placeholder="Syarikat Perniagaan ABC Sdn. Bhd."
              propsInput={{
                control: control,
              }}
            />
          </Col>

          <Col flex={1}>
            <TInput
              label="Short Name"
              errors={errors}
              name="short_name"
              placeholder="ABC"
              propsInput={{
                control: control,
              }}
            />
          </Col>
        </Row>

        <Row gutter={4}>
          <Col flex={2}>
            <TPhoneInput
              label="Office Phone"
              errors={errors}
              name="office_phone"
              numType="landline"
              placeholder="03-62754070"
              setValue={setValue}
              propsInput={{
                control: control,
              }}
            />
          </Col>

          <Col flex={2}>
            <TPhoneInput
              label="Company Mobile Phone"
              errors={errors}
              name="mobile_phone"
              numType="mobile"
              placeholder="013-2456789"
              setValue={setValue}
              propsInput={{
                control: control,
              }}
            />
          </Col>
          <Col flex={2}>
            <TInput
              label="Company Email"
              errors={errors}
              name="email"
              placeholder="abc@company.com"
              propsInput={{
                control: control,
              }}
            />
          </Col>
        </Row>

        <Row gutter={2}>
          <Col flex={1}>
            <TypeContainer>
              <StyledLabel>Type</StyledLabel>
              <Controller
                control={control}
                name="type"
                render={({ onChange, onBlur, value }) => {
                  return (
                    <Radio.Group
                      value={value}
                      onChange={(e) => {
                        setValue("type", e.target.value);
                      }}
                    >
                      <StyledRadio value="Government" name="Government">
                        Government
                      </StyledRadio>
                      <StyledRadio value="Business" name="Business">
                        Business
                      </StyledRadio>
                      <StyledRadio value="Personal" name="Personal">
                        Personal
                      </StyledRadio>
                    </Radio.Group>
                  );
                }}
              />
            </TypeContainer>
          </Col>
          <Col flex={3}>
            <TInput
              label="Address"
              option="textarea"
              row={3}
              size="large"
              errors={errors}
              name="address"
              placeholder="Where is this company located"
              propsInput={{
                control: control,
              }}
            />
          </Col>
        </Row>

        {fields.length > 0 && (
          <ContactContainer>
            <h1>Contact Person</h1>
            {fields.map((item, index) => {
              return (
                <ItemContainer key={item.id}>
                  <Row gutter={4}>
                    <Col flex={5}>
                      <TInput
                        label="Name"
                        errors={errors}
                        name={`contact_person[${index}].name`}
                        placeholder="Contact person name"
                        defaultValue={item.name}
                        propsInput={{
                          control: control,
                        }}
                      />
                    </Col>
                    <Col flex={2}>
                      <TPhoneInput
                        label="Phone number"
                        errors={errors}
                        name={`contact_person[${index}].mobile_phone`}
                        numType="mobile"
                        placeholder="013-2456789"
                        setValue={setValue}
                        defaultValue={item.mobile_phone}
                        propsInput={{
                          control: control,
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col flex={2}>
                      <TInput
                        label="Email"
                        errors={errors}
                        name={`contact_person[${index}].email`}
                        placeholder="Contact person email"
                        defaultValue={item.email}
                        propsInput={{
                          control: control,
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col flex={1}>
                      <RemoveButton type="link" onClick={() => remove(index)}>
                        Remove
                      </RemoveButton>
                    </Col>
                  </Row>
                </ItemContainer>
              );
            })}
          </ContactContainer>
        )}

        <Button
          type="outlined"
          icon={<PlusOutlined />}
          onClick={() => {
            append({
              name: null,
              mobile_phone: null,
              email: null,
            });
          }}
          disabled={fields.length > 2}
        >
          Add Contact Person
        </Button>

        <div className="button">
          <Button
            size="medium"
            type="primary"
            htmlType="submit"
            loading={
              props.update
                ? notificationState.loading.updateClient
                : notificationState.loading.addClient
            }
          >
            {props.update ? "Update Client" : "Add Client"}
          </Button>
        </div>
      </form>
    </StyledContainer>
  );
}

const StyledContainer = styled.div`
  border-radius: 3px;
  background: ${(props) => props.theme.colors.grey[400]};
  padding: 20px;
  width: 100%;
  box-shadow: 0 1px 3px 0 rgba(244, 245, 247, 0.1),
    0 1px 2px 0 rgba(244, 245, 247, 0.06);

  .alert {
    margin-bottom: 15px;
  }

  .button {
    display: flex;
    justify-content: flex-end;
  }

  .input-container {
    padding-bottom: 1.5rem;

    .label-container {
      display: flex;
      justify-content: space-between;

      .label {
        font-weight: 600;
        padding-bottom: 0.25rem;
        display: block;
        font-size: 14px;
      }

      .error {
        color: ${(props) => props.theme.colors.red[700]};
        font-style: italic;
      }
    }

    .input,
    .input-error {
      width: 100%;
      padding: 0.5rem 1rem;
      border-radius: 0.25rem;
      background: ${(props) => props.theme.colors.grey[100]};
      border: 1px solid ${(props) => props.theme.colors.grey[500]};

      ::placeholder {
        color: ${(props) => props.theme.colors.grey[500]};
        opacity: 1; /* Firefox */
      }

      &:focus {
        outline: none !important;
        border: ${(props) => `1px solid
          ${props.theme.colors.primary}`};
      }

      &.input-error,
      .input-error:focus {
        outline: none !important;
        border: ${(props) => `1px solid
          ${props.theme.colors.error.primary}`};
      }
    }
  }
`;

const ContactContainer = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  padding: ${(props) => props.theme.spacing[3]};
  border: 1px solid ${(props) => props.theme.colors.grey[500]};
  margin-bottom: ${(props) => props.theme.spacing[2]};

  h1 {
    font-size: 1.1rem;
    color: ${(props) => props.theme.colors.grey[800]};
  }
`;

const ItemContainer = styled.div`
  padding: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.grey[200]};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
`;

const RemoveButton = styled(Button)`
  padding: 0;
  margin: 0;
  color: ${(props) => props.theme.colors.grey[500]};
  transition: none;

  :hover {
    color: ${(props) => props.theme.colors.red[600]};
  }
`;

const StyledRadio = styled(Radio)`
  display: block;
  height: 30px;
  line-height: 30px;
  color: ${(props) => props.theme.colors.grey[700]};
  font-weight: 600;
`;

const StyledLabel = styled.label`
  font-weight: 500;
  color: ${(props) => props.theme.colors.grey[600]};
  margin-bottom: ${(props) => props.theme.spacing[1]};
`;

const TypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => props.theme.spacing[3]};
`;

export default AddClientForm;
